/**
 * Cloud-relay print queue.
 *
 * When there is no local print agent on this PC, a print job is queued on the
 * backend instead and the agent running on the printer's own PC claims it,
 * prints locally, and reports the result. This is what lets a workstation print
 * to a printer on another PC without an agent of its own (a browser cannot reach
 * a remote agent over the LAN).
 */
import { post } from '../services/httpMethods';
import type { ApiResponse } from '../types/api';

export type PrintJobKind = 'raw' | 'document';

export interface EnqueuePrintJobInput {
  kind: PrintJobKind;
  /** raw | pdf | image (defaults sensibly per kind). */
  format?: string;
  /** Windows share name on the printer host. */
  shareName: string;
  /** PC name / IP of the machine that owns the printer. */
  host?: string;
  /** Base64 payload: ESC/POS bytes (raw) or PDF/image bytes (document). */
  payload: string;
  printerLabel?: string;
  source?: string;
}

export interface EnqueuePrintJobResult {
  id: string;
  status: string;
  targetHost?: string | null;
  /** True when a dashboard on the printer's PC received the job immediately. */
  delivered: boolean;
}

/** Queue a print job and push it to the dashboard open on the printer's PC. */
export function enqueuePrintJob(
  input: EnqueuePrintJobInput,
): Promise<ApiResponse<EnqueuePrintJobResult>> {
  return post<EnqueuePrintJobResult>('/system/print-jobs', input);
}

/** Queue a PDF/image document for the dashboard on the printer's PC. */
export function enqueueDocumentBase64(
  base64: string,
  shareName: string,
  host?: string,
  printerLabel?: string,
  source?: string,
): Promise<ApiResponse<EnqueuePrintJobResult>> {
  return enqueuePrintJob({
    kind: 'document',
    format: 'pdf',
    shareName,
    host,
    payload: base64,
    printerLabel,
    source,
  });
}
